<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import * as Sentry from '@sentry/vue';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import ScheduleFormFields from '@/components/schedules/ScheduleFormFields.vue';
import ScheduleTypeIcon from '@/components/schedules/ScheduleTypeIcon.vue';
import BulkEditDialog from './BulkEditDialog.vue';
import { isSubmitShortcut, submitShortcutLabel } from '@/utils/submitShortcut';
import { useSubmitShortcutFromMenus } from '@/composables/useSubmitShortcutFromMenus';
import {
    bulkApiErrorMessage,
    postBulkBreakdown,
    readBulkApiError,
    type BulkBreakdown,
    type BulkEditableField,
    type BulkOperation,
} from '@/composables/api/useBulkActionApi';
import type { BulkSelectionPayload } from '@/composables/useBulkSelection';
import {
    EDITABLE_SCHEDULE_KINDS,
    SCHEDULE_KIND_LABELS,
    bulkTabEndsBeforeStart,
    changedFields,
    describeScheduleBulkTab,
    emptyScheduleBulkTab,
    scheduleBulkTabToChanges,
    scheduleKindColor,
    validateScheduleBulkTab,
    type EditableScheduleKind,
    type ScheduleBulkError,
    type ScheduleBulkTab,
} from '@/utils/customPlaylistSchedule';

/**
 * Bulk edit schedules with the schedule editor's own form.
 *
 * A selection can mix kinds of schedule, and the editor shows different fields for each kind.
 * So the dialog has one tab per kind in the selection, such as "Recurring 8" and "One-off 4",
 * and each tab is the editor's form for that kind. Every field starts as "Keep as is", and only
 * the fields changed are sent. A tab's changes carry its kind, so they reach only rows of that
 * kind: rows of a kind whose tab was left alone are not changed.
 *
 * The counts per kind come from `POST …/bulk-action/breakdown`, which resolves the selection the
 * way the dry run does. "Check the changes" hands the changes of every tab to the confirmation.
 *
 * On the custom list search, lists can be ticked too. The dialog then has a "Custom Lists" tab
 * after the schedule tabs. It is the custom list bulk edit's field rows (BulkEditDialog, embedded).
 * A tab with nothing ticked is not shown: with only lists ticked, the schedules are not counted
 * and only the "Custom Lists" tab shows. The submit carries the list edit and the schedule edit
 * separately, each null when its tabs were left alone.
 *
 * With a `listMemoryKey`, the "Custom Lists" tab opens on the last list edit sent to the
 * confirmation (bulkEditMemory). The schedule tabs are never remembered: every opening starts
 * them as "Keep as is".
 */
const props = withDefaults(defineProps<{
    /** How many schedules are selected, for the heading. */
    count: number;
    /** The schedule `selection`, read when the dialog opens. */
    buildSelection: () => BulkSelectionPayload | null;
    /** How many lists are ticked. 0 hides the "Custom Lists" tab. */
    listCount?: number;
    /** The list fields this user may edit. Empty hides the "Custom Lists" tab. */
    listFields?: BulkEditableField[];
    /** Where the "Custom Lists" tab remembers its last edit. Null remembers nothing. */
    listMemoryKey?: string | null;
}>(), { listCount: 0, listFields: () => [], listMemoryKey: null });

const open = defineModel<boolean>('open', { default: false });

/** One kind's edit, as the confirmation needs it. */
export interface BulkEditPayload {
    operation: BulkOperation;
    valueLabels: Record<string, string>;
    summary: string[];
}

const emit = defineEmits<{
    (e: 'submit', payload: { schedules: BulkEditPayload | null; lists: BulkEditPayload | null }): void;
}>();

const SCHEDULE_SLUG = 'custom_playlist_schedule';
/** Every kind, in the order the tabs show them. Station changes are counted but cannot be edited here. */
const KIND_ORDER = [...EDITABLE_SCHEDULE_KINDS, 'stationchange'] as const;
/** The "Custom Lists" tab's value. */
const LISTS_TAB = 'lists';
type TabKind = (typeof KIND_ORDER)[number] | typeof LISTS_TAB;

const showsListsTab = computed(() => props.listCount > 0 && props.listFields.length > 0);
const hasSchedules = computed(() => props.count > 0);
const listEditor = ref<InstanceType<typeof BulkEditDialog> | null>(null);

const breakdown = ref<BulkBreakdown | null>(null);
const loadError = ref<string | null>(null);
/** The server's message when "select all matching" now matches a different number of rows. */
const countChangedMessage = ref<string | null>(null);
const tabs = ref<Partial<Record<EditableScheduleKind, ScheduleBulkTab>>>({});
const activeKind = ref<TabKind | null>(null);
const attempted = ref(false);
const formError = ref<string | null>(null);

/** The kinds in the selection, with their row counts. */
const kindTabs = computed<{ kind: (typeof KIND_ORDER)[number]; count: number }[]>(() =>
    KIND_ORDER.filter((kind) => (breakdown.value?.kinds[kind] ?? 0) > 0).map((kind) => ({ kind, count: breakdown.value!.kinds[kind]! })),
);

/**
 * Bumped every time the dialog opens. The counts are asked for on each opening, and an answer
 * that lands after the dialog was closed and opened again, possibly on another selection, is
 * for an opening that no longer shows. It is dropped.
 */
let openCount = 0;

// Each opening starts over: counts for the selection as it is now, and every schedule field kept.
watch(open, async (isOpen) => {
    if (!isOpen) return;
    const thisOpening = ++openCount;
    breakdown.value = null;
    loadError.value = null;
    countChangedMessage.value = null;
    attempted.value = false;
    formError.value = null;
    tabs.value = Object.fromEntries(EDITABLE_SCHEDULE_KINDS.map((kind) => [kind, emptyScheduleBulkTab(kind)]));
    const selection = hasSchedules.value ? props.buildSelection() : null;
    if (!selection) {
        // With lists ticked and no schedules, there is nothing to count: only the lists tab shows.
        if (showsListsTab.value) {
            breakdown.value = { selected: 0, skipped_for_access: 0, kinds: {}, not_found: 0 };
            activeKind.value = LISTS_TAB;
            return;
        }
        loadError.value = 'Select some schedules first.';
        return;
    }
    try {
        const counted = await countSelection(selection, thisOpening);
        if (thisOpening !== openCount || !counted) return;
        breakdown.value = counted;
        activeKind.value = kindTabs.value[0]?.kind ?? (showsListsTab.value ? LISTS_TAB : null);
    } catch (error) {
        Sentry.captureException(error);
        if (thisOpening !== openCount) return;
        loadError.value = bulkApiErrorMessage(error, "Couldn't count the selected schedules.");
    }
});

/**
 * The counts per kind. "Select all matching" sends the count the user saw, and the server answers
 * 409 when the search now matches a different number. The tabs then count the new selection and
 * say so. The confirmation step asks the user to confirm the new count before anything is saved.
 */
async function countSelection(selection: BulkSelectionPayload, thisOpening: number): Promise<BulkBreakdown | null> {
    try {
        return await postBulkBreakdown(SCHEDULE_SLUG, selection);
    } catch (error) {
        const { status, body } = readBulkApiError(error);
        if (status !== 409 || body.code !== 'count_changed' || typeof body.current_count !== 'number' || selection.mode !== 'search') throw error;
        if (thisOpening !== openCount) return null;
        countChangedMessage.value = body.message ?? `The search now matches ${body.current_count.toLocaleString()} schedules.`;
        return await postBulkBreakdown(SCHEDULE_SLUG, { ...selection, expected_count: body.current_count });
    }
}

function isEditable(kind: TabKind): kind is EditableScheduleKind {
    return (EDITABLE_SCHEDULE_KINDS as readonly string[]).includes(kind);
}

function tabFor(kind: EditableScheduleKind): ScheduleBulkTab {
    return tabs.value[kind]!;
}

function setTab(kind: EditableScheduleKind, patch: Partial<ScheduleBulkTab>): void {
    tabs.value = { ...tabs.value, [kind]: { ...tabFor(kind), ...patch } };
}

const errorsByKind = computed<Partial<Record<EditableScheduleKind, ScheduleBulkError[]>>>(() =>
    attempted.value
        ? Object.fromEntries(kindTabs.value.filter((tab) => isEditable(tab.kind)).map((tab) => [tab.kind, validateScheduleBulkTab(tabFor(tab.kind as EditableScheduleKind))]))
        : {},
);

function errorForKind(kind: EditableScheduleKind) {
    return (...fields: string[]): string | null =>
        errorsByKind.value[kind]?.find((error) => fields.includes(error.field))?.message ?? null;
}

function hasChanges(kind: TabKind): boolean {
    if (kind === LISTS_TAB) return listEditor.value?.hasChanges ?? false;
    return isEditable(kind) && changedFields(tabFor(kind)).length > 0;
}

/**
 * A tab that sets both dates with the end before the start. The tab says so under its end date
 * as it happens, and the button stays disabled until it is fixed, because no row could take it.
 */
const hasEndBeforeStart = computed(() =>
    kindTabs.value.some((tab) => isEditable(tab.kind) && bulkTabEndsBeforeStart(tabFor(tab.kind))),
);

/** False while the button is disabled: the breakdown is still loading, there is nothing to edit, or a tab's dates are backwards. */
const canSubmit = computed(() => Boolean(breakdown.value) && (kindTabs.value.length > 0 || showsListsTab.value) && !hasEndBeforeStart.value);

const submitShortcut = submitShortcutLabel();

/** Cmd+Enter checks the changes, as the button does, from any tab, the Custom Lists tab included. */
function onKeydown(event: KeyboardEvent): void {
    if (!isSubmitShortcut(event)) return;
    event.preventDefault();
    if (canSubmit.value) submit();
}

useSubmitShortcutFromMenus(() => open.value, onKeydown);

function submit(): void {
    attempted.value = true;
    formError.value = null;
    const editedKinds = kindTabs.value.map((tab) => tab.kind).filter((kind): kind is EditableScheduleKind => isEditable(kind) && hasChanges(kind));
    const kindWithErrors = editedKinds.find((kind) => validateScheduleBulkTab(tabFor(kind)).length > 0);
    if (kindWithErrors) {
        activeKind.value = kindWithErrors;
        return;
    }
    const listEdit = showsListsTab.value && listEditor.value ? listEditor.value.collect() : null;
    if (showsListsTab.value && listEditor.value && listEdit === null) {
        activeKind.value = LISTS_TAB;
        return;
    }
    const listChanges = listEdit?.changes ?? [];
    if (editedKinds.length === 0 && listChanges.length === 0) {
        formError.value = 'Change at least one field on one of the tabs.';
        return;
    }
    if (showsListsTab.value) listEditor.value?.remember();
    const changes = editedKinds.flatMap((kind) => scheduleBulkTabToChanges(tabFor(kind)));
    // Each line says which kind it is for, since the same field can change on several tabs.
    const summary = editedKinds.flatMap((kind) => describeScheduleBulkTab(tabFor(kind)).map((line) => `${SCHEDULE_KIND_LABELS[kind]}: ${line}`));
    emit('submit', {
        schedules: editedKinds.length > 0 ? { operation: { action: 'update', changes }, valueLabels: {}, summary } : null,
        lists: listEdit && listChanges.length > 0
            ? {
                operation: { action: 'update', changes: listChanges },
                valueLabels: listEdit.valueLabels,
                summary: listEdit.summary.map((line) => `Custom Lists: ${line}`),
            }
            : null,
    });
}

/** "3 lists and 14 schedules", "3 lists" or "14 schedules", for the heading. */
const headingCount = computed(() => {
    const parts: string[] = [];
    if (showsListsTab.value) parts.push(`${props.listCount.toLocaleString()} list${props.listCount === 1 ? '' : 's'}`);
    if (hasSchedules.value || !showsListsTab.value) parts.push(`${props.count.toLocaleString()} schedule${props.count === 1 ? '' : 's'}`);
    return parts.join(' and ');
});
</script>

<template>
    <Dialog v-model:open="open">
        <!-- Sized like the inspector's center peek: most of the screen, and the whole of a phone's.
             The form scrolls inside it, and the buttons stay at the bottom. -->
        <DialogContent
            class="flex min-h-0 max-w-none flex-col gap-4 overflow-hidden sm:max-w-none h-[100dvh] w-[100vw] rounded-none max-sm:top-0 max-sm:translate-y-0 sm:h-[75vh] sm:w-[75vw] sm:rounded-3xl"
            data-testid="schedule-bulk-edit-dialog"
            @keydown="onKeydown"
        >
            <DialogHeader>
                <DialogTitle>Bulk edit {{ headingCount }}</DialogTitle>
                <DialogDescription>
                    Each tab is one type of schedule in your selection<template v-if="showsListsTab">, or the custom lists</template>. Change a field on a tab and it changes for that tab's rows only.
                    Nothing is saved until you confirm on the next step.
                </DialogDescription>
            </DialogHeader>

            <p v-if="countChangedMessage && !loadError" class="text-muted-foreground text-sm" role="status" data-testid="schedule-bulk-count-changed">
                {{ countChangedMessage }} The tabs count the schedules it matches now.
            </p>
            <p v-if="loadError" class="text-destructive text-sm" role="alert" data-testid="schedule-bulk-load-error">{{ loadError }}</p>
            <p v-else-if="!breakdown" class="text-muted-foreground text-sm">Counting the selected schedules…</p>
            <p v-else-if="kindTabs.length === 0 && !showsListsTab" class="text-muted-foreground text-sm">None of the selected schedules can be edited.</p>

            <Tabs
                v-else
                :model-value="activeKind ?? undefined"
                class="flex min-h-0 flex-1 flex-col gap-3"
                @update:model-value="(value) => (activeKind = value as TabKind)"
            >
                <TabsList class="h-auto flex-wrap justify-start" data-testid="schedule-bulk-tabs">
                    <TabsTrigger v-for="tab in kindTabs" :key="tab.kind" :value="tab.kind" :data-testid="`schedule-bulk-tab-${tab.kind}`">
                        <span class="flex items-center gap-1.5">
                            <span class="h-3.5 w-1 shrink-0 rounded-sm" :style="{ backgroundColor: scheduleKindColor(tab.kind) }" aria-hidden="true" />
                            <ScheduleTypeIcon :kind="tab.kind" decorative />
                            {{ SCHEDULE_KIND_LABELS[tab.kind] }}
                            <span class="text-muted-foreground tabular-nums">{{ tab.count.toLocaleString() }}</span>
                            <span v-if="hasChanges(tab.kind)" class="bg-primary size-1.5 rounded-full" aria-label="Changed" data-testid="schedule-bulk-tab-changed" />
                        </span>
                    </TabsTrigger>
                    <TabsTrigger v-if="showsListsTab" :value="LISTS_TAB" data-testid="schedule-bulk-tab-lists">
                        <span class="flex items-center gap-1.5">
                            Custom Lists
                            <span class="text-muted-foreground tabular-nums">{{ listCount.toLocaleString() }}</span>
                            <span v-if="hasChanges(LISTS_TAB)" class="bg-primary size-1.5 rounded-full" aria-label="Changed" data-testid="schedule-bulk-tab-changed" />
                        </span>
                    </TabsTrigger>
                </TabsList>

                <TabsContent v-for="tab in kindTabs" :key="tab.kind" :value="tab.kind" class="-mx-1 min-h-0 flex-1 overflow-y-auto px-1">
                    <p v-if="!isEditable(tab.kind)" class="text-muted-foreground text-sm" data-testid="schedule-bulk-station-change">
                        Station changes come from the player switcher and can't be edited here. They will be left as they are.
                    </p>
                    <div v-else class="grid grid-cols-1 gap-4 sm:grid-cols-2" :data-testid="`schedule-bulk-form-${tab.kind}`">
                        <ScheduleFormFields
                            mode="bulk"
                            :form="tabFor(tab.kind).form"
                            :bulk="tabFor(tab.kind).bulk"
                            :error-for="errorForKind(tab.kind)"
                            @update:form="(form) => setTab(tab.kind as EditableScheduleKind, { form })"
                            @update:bulk="(bulk) => setTab(tab.kind as EditableScheduleKind, { bulk: bulk! })"
                        />
                    </div>
                </TabsContent>
                <!-- Kept mounted while another tab shows, so its rows survive a tab switch. -->
                <TabsContent
                    v-if="showsListsTab"
                    :value="LISTS_TAB"
                    force-mount
                    class="-mx-1 min-h-0 flex-1 overflow-y-auto px-1 data-[state=inactive]:hidden"
                >
                    <BulkEditDialog ref="listEditor" embedded :fields="listFields" :count="listCount" :memory-key="listMemoryKey" />
                </TabsContent>
            </Tabs>

            <p v-if="formError" class="text-destructive text-sm" role="alert" data-testid="schedule-bulk-error">{{ formError }}</p>

            <DialogFooter>
                <Button variant="outline" data-testid="schedule-bulk-cancel" @click="open = false">Cancel</Button>
                <Button :disabled="!canSubmit" :title="`Check the changes (${submitShortcut})`" data-testid="schedule-bulk-submit" @click="submit">Check the changes</Button>
            </DialogFooter>
        </DialogContent>
    </Dialog>
</template>
